import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert
} from '@mui/material';
import HexagonIcon from '@mui/icons-material/Hexagon';

const HexToPantonePage: React.FC = () => {
  const [hexInput, setHexInput] = useState<string>('');
  const [pantoneResult, setPantoneResult] = useState<string>('');
  const [isValidHex, setIsValidHex] = useState<boolean>(true);
  const [colorPreview, setColorPreview] = useState<string>('transparent');

  useEffect(() => {
    if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(hexInput)) {
      setIsValidHex(true);
      setColorPreview(hexInput);
    } else if (hexInput === '') {
      setIsValidHex(true); // No error for empty input
      setColorPreview('transparent');
    } else {
      setIsValidHex(false);
      setColorPreview('transparent');
    }
  }, [hexInput]);

  const handleConvert = () => {
    if (!isValidHex || !hexInput) {
      setPantoneResult('');
      return;
    }
    // Mock Pantone conversion
    // In a real application, you would use a color library or API here.
    const mockPantoneColors: { [key: string]: string } = {
      '#FF0000': 'Pantone 185 C',
      '#00FF00': 'Pantone 354 C',
      '#0000FF': 'Pantone 286 C',
      '#FFFF00': 'Pantone Yellow C',
      '#000000': 'Pantone Black C',
      '#FFFFFF': 'Pantone White C',
    };

    const normalizedHex = hexInput.toUpperCase();
    if (mockPantoneColors[normalizedHex]) {
      setPantoneResult(mockPantoneColors[normalizedHex]);
    } else {
      // Simple mock logic: take first 3 chars of hex and assign a mock Pantone
      const base = normalizedHex.substring(1, 4);
      setPantoneResult(`Mock Pantone ${base} (approx.)`);
    }
  };

  const handleClear = () => {
    setHexInput('');
    setPantoneResult('');
    setColorPreview('transparent');
    setIsValidHex(true);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HexagonIcon sx={{ mr: 1.5, fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              HEX to Pantone Converter
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter a HEX color code to find its closest (mocked) Pantone match.
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Enter HEX Color Code (e.g., #RRGGBB)"
                variant="outlined"
                value={hexInput}
                onChange={(e) => setHexInput(e.target.value)}
                error={!isValidHex}
                helperText={!isValidHex ? 'Invalid HEX format (e.g., #FF0000 or #F00)' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={2}
                sx={{
                  height: 50,
                  width: '100%',
                  backgroundColor: colorPreview,
                  border: '1px solid lightgray',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.3s ease'
                }}
              >
                {!isValidHex && hexInput !== '' && (
                    <Typography variant="caption" sx={{color: 'black'}}>Invalid</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleConvert}
              disabled={!isValidHex || !hexInput}
            >
              Convert to Pantone
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
            >
              Clear
            </Button>
          </Box>

          {pantoneResult && (
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="h6">Pantone Match:</Typography>
              <Typography>{pantoneResult}</Typography>
              <Typography variant="caption" sx={{display: 'block', mt: 1}}>
                Note: This is a mocked result for demonstration purposes.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HexToPantonePage;
